import { Fragment, useState } from "react";
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ContentFlag } from "~/components/content-flag";
import { FormatDate } from "~/lib/date";
import {
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { ActionMenu } from "~/components/action-menu";
import { useCanTriggerAction } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/TriggerAction";
import { CanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";
import { CanDelete } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Delete";
import {
  ActionType,
  GetClientIntegrationQuery,
  useTriggerActionLazyQuery,
} from "~/operations";
import { useSnackbar } from "notistack";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { LoadingButton } from "~/components/loading-button";
import { ticketingIntegrationTypes } from "~/pages/integrations/utils/integrationConstants";
import { formatStatus } from "../utils/statuses";

interface IntegrationHeaderProps {
  integration: GetClientIntegrationQuery["clientIntegration"]["integration"];
  integrationTypeId: string | undefined;
  scanAction?: ActionType;
  actions?: {
    deleteIntegration: () => void;
    editIntegration: () => void;
  };
}

export const HostedIntegrationHeader = ({
  integration,
  actions,
  integrationTypeId,
  scanAction = ActionType.RunScan,
}: IntegrationHeaderProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [triggeringScan, setTriggeringScan] = useState(false);
  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    variables: { input: { mrn: integration.mrn, type: scanAction } },
  });
  const [triggerClientIntegrationPause] = useTriggerActionLazyQuery({
    variables: { input: { mrn: integration.mrn, type: ActionType.Pause } },
  });
  const [triggerClientIntegrationEnable] = useTriggerActionLazyQuery({
    variables: { input: { mrn: integration.mrn, type: ActionType.Unpause } },
  });
  // expose the error buried in the integration messages if it exists
  const hasError =
    integration.status !== "ACTIVE" &&
    integration.messages?.find((x) => x.status.toLowerCase() === "error");

  const actionType =
    scanAction === ActionType.RunExport
      ? "export"
      : scanAction === ActionType.RunImport
        ? "import"
        : "scan";

  const triggerScan = async () => {
    if (triggeringScan) {
      return;
    }
    setTriggeringScan(true);
    try {
      const { error } = await triggerClientIntegrationScan();
      if (error) {
        throw new Error(error.message);
      }

      enqueueSnackbar(`Successfully scheduled integration ${actionType}`, {
        variant: "success",
      });
    } catch (error) {
      const action = actionType === "scan" ? "scan of integration" : actionType;
      let msg = `Failed to schedule ${action}`;
      if (error instanceof Error) {
        msg = `${msg}: ${error.message}`;
      }
      enqueueSnackbar(msg, {
        variant: "error",
      });
    } finally {
      setTriggeringScan(false);
    }
  };

  const hasIntegrationTriggerActionPermission = useCanTriggerAction();

  const isTicketingIntegration = ticketingIntegrationTypes.includes(
    integration.type,
  );

  const SHOW_TRIGGER_STATES = ["ACTIVE", "NOT_READY", "ERROR"];

  const showTrigger =
    SHOW_TRIGGER_STATES.includes(integration.status) &&
    hasIntegrationTriggerActionPermission &&
    !isTicketingIntegration;

  const unpauseScanning = async () => {
    try {
      const { error } = await triggerClientIntegrationEnable();
      if (error) {
        throw new Error(error.message);
      }
      const action = actionType === "scan" ? "scanning" : actionType;
      const msg = `Successfully enabled integration ${action}`;
      enqueueSnackbar(msg, {
        variant: "success",
      });
    } catch (error) {
      const action =
        actionType === "scan" ? "scanning on integration" : actionType;
      let msg = `Failed to enable ${action}`;
      if (error instanceof Error) {
        msg = `${msg}: ${error.message}`;
      }
      enqueueSnackbar(msg, {
        variant: "error",
      });
    }
  };

  const pauseScanning = async () => {
    if (integration.status === "PAUSED") {
      return unpauseScanning();
    }
    try {
      const { error } = await triggerClientIntegrationPause();
      if (error) {
        throw new Error(error.message);
      }
      const action = actionType === "scan" ? "scanning" : actionType;
      enqueueSnackbar(`Successfully paused integration ${action}`, {
        variant: "success",
      });
    } catch (error) {
      const action =
        actionType === "scan" ? "scanning on integration" : actionType;
      let msg = `Failed to pause ${action}`;
      if (error instanceof Error) {
        msg = `${msg}: ${error.message}`;
      }
      enqueueSnackbar(msg, {
        variant: "error",
      });
    }
  };

  let actionMenu = [
    {
      label: "Copy MRN",
      action: () => navigator.clipboard.writeText(`${integration.mrn}`),
      dataName: `${integrationTypeId}-copyMrn`,
    },
  ];

  if (hasIntegrationTriggerActionPermission && !isTicketingIntegration) {
    const action =
      actionType === "scan"
        ? "Scanning"
        : `${capitalizeFirstLetter(actionType)}s`;
    actionMenu.push({
      label:
        integration.status != "PAUSED" ? `Pause ${action}` : `Enable ${action}`,
      action: () => pauseScanning(),
      dataName: `${integrationTypeId}-pause`,
    });
  }

  return (
    <Box>
      {hasError && (
        <Alert severity="error" variant="outlined" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {capitalizeFirstLetter(hasError.message)}
        </Alert>
      )}
      <Grid container>
        <Grid
          size={{
            xs: 12,
            md: "grow",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              id={`${integrationTypeId}-integration-name`}
              variant="h4"
              fontWeight={700}
              sx={{ display: "inline", pr: 2, overflowWrap: "anywhere" }}
            >
              {integration.name}
            </Typography>
            <ContentFlag
              flag={formatStatus(integration.status)}
              color={integration.status}
            />
          </Box>

          <Grid container columnGap={2} sx={{ mb: { sm: 2, md: 0 } }}>
            {!ticketingIntegrationTypes.includes(integration.type) && (
              <Grid
                size={{
                  xs: 12,
                  sm: "auto",
                }}
              >
                <AccessTimeIcon
                  sx={{
                    fontSize: 16,
                    height: 28,
                    color: "text.secondary",
                    verticalAlign: "middle",
                  }}
                />
                <Typography
                  display="inline"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.75 }}
                >
                  {/* Last scanned: April 05, 10:10am */}
                  Last {`${actionType === "scan" ? "scann" : actionType}ed`}:{" "}
                  {FormatDate(integration.lastCheckin)}
                </Typography>
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              {integration.createdBy?.name !== "" && (
                <Fragment>
                  <PersonIcon
                    sx={{
                      fontSize: 16,
                      height: 28,
                      color: "text.secondary",
                      verticalAlign: "middle",
                    }}
                  />
                  <Typography
                    display="inline"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    Created by: {integration.createdBy?.name}
                  </Typography>
                </Fragment>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            color: "text.secondary",
            mb: 1.625,
          }}
          size={{
            xs: 12,
            md: 5,
          }}
        >
          <Grid
            display="flex"
            alignItems="center"
            justifyContent="end"
            size="grow"
          >
            <CanUpdate>
              <IconButton
                size="large"
                onClick={actions?.editIntegration}
                sx={{ mx: 0.5 }}
              >
                <EditIcon />
              </IconButton>
            </CanUpdate>
            <CanDelete>
              <IconButton
                size="large"
                sx={{
                  mx: 0.5,
                  color: (theme) => theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.error.main, 0.15),
                  },
                }}
                onClick={actions?.deleteIntegration}
              >
                <DeleteIcon />
              </IconButton>
            </CanDelete>

            <ActionMenu
              id={`${integrationTypeId}-action-menu`}
              actions={actionMenu}
            />

            {/* We only show the divider if anything exists to the right of it */}
            {/* Scan Trigger only visible when user has permission and status is active or warning */}
            {showTrigger && (
              <Fragment>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <LoadingButton
                  buttonText="Schedule Now"
                  loading={triggeringScan}
                  onClick={() => triggerScan()}
                  data-name={`${integrationTypeId}-triggerscan`}
                  sx={{
                    position: "relative",
                    color: "common.white",
                    background:
                      "linear-gradient(133.55deg, #9147FF 0%, #4C35E8 100%)", //<--- palette breaking
                    ml: 1,
                  }}
                />
              </Fragment>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
